import _ from 'lodash'
import { actions } from './reducer'
import ssapi from 'lib/api/secondstepApi'
import { push } from 'connected-react-router'
import { addNotification } from 'store/notifications'
import { getReadableErrorMessage } from 'utils/errorUtils'
import { AxiosResponse } from 'axios'
import {
  Site,
  SiteFilters,
  SiteUpdate,
  SiteUser,
  MergeSiteParameters,
  SendEmailRequest,
  RootState,
} from 'types'
import { PatchResponse } from 'types/api'
import { CreateAccessCodeResult } from 'types/site'

export const fetchSite = (siteId: number) => {
  return async dispatch => {
    dispatch(actions.fetchingSites())
    try {
      const response: AxiosResponse<Site> = await ssapi.getSite(siteId)
      dispatch(actions.fetchingSitesSuccess([response.data]))
    } catch (error) {
      dispatch(actions.fetchingSitesError(error))
    }
  }
}

export const fetchSites = (criteria: SiteFilters) => {
  return async dispatch => {
    dispatch(actions.fetchingSites())
    dispatch(actions.setSiteSearchCriteria(criteria))
    try {
      const response: AxiosResponse<Site[]> = await ssapi.getSites(criteria)
      dispatch(actions.fetchingSitesSuccess(response.data))
      if (response.data.length === 1)
        dispatch(push(`/css-admin/sites/${response.data[0].id}`))
    } catch (error) {
      dispatch(actions.fetchingSitesError(error))
    }
  }
}

export const mergeSite = (mergeSiteParameters: MergeSiteParameters) => {
  return async dispatch => {
    const { siteId, primarySiteId, isPreview } = mergeSiteParameters
    dispatch(actions.mergingSite())
    try {
      const response = await ssapi.mergeSite({
        siteId,
        primarySiteId,
        isPreview,
      })

      if (!isPreview) {
        dispatch(
          addNotification({
            color: 'success',
            text: `You have sucessfully merged Site ${siteId} into Site ${primarySiteId}`,
          }),
        )
      }
      const data = response.data !== '' ? response.data : null
      if (data) {
        data.isPreview = isPreview
        dispatch(actions.mergingSiteSuccess(data))
      } else {
        dispatch(actions.mergingSiteSuccess({ isPreview }))
      }
    } catch (error) {
      const errorMessage = getReadableErrorMessage(
        error,
        'An error occurred during merge',
      )
      dispatch(
        addNotification({
          color: 'danger',
          text: errorMessage,
        }),
      )
      dispatch(actions.mergingSiteError(errorMessage))
    }
  }
}

export const updateSite = (site: SiteUpdate) => {
  return async dispatch => {
    dispatch(actions.updatingSite())
    try {
      const response: AxiosResponse<Site> = await ssapi.updateSite(site)
      dispatch(actions.updatingSiteSuccess(response.data))
      dispatch(
        addNotification({
          color: 'success',
          text: 'Site successfully updated',
        }),
      )
      dispatch(fetchSites({ siteId: site.id, includeFutureLicenses: true }))
    } catch (error) {
      const message = getReadableErrorMessage(error, 'Unknown error')
      dispatch(
        addNotification({
          color: 'danger',
          text: `An error has occurred during update: ${message}`,
        }),
      )
      dispatch(actions.updatingSiteError(error))
    }
  }
}

export const fetchSiteUsers = (siteId: number) => {
  return async (dispatch, getState: () => RootState) => {
    const state = getState()
    const siteUsersData = state.sitesManager.siteUsers.data
    if (siteUsersData && siteUsersData[siteId]) return
    dispatch(actions.fetchingSiteUsers())

    try {
      const response = await ssapi.getSiteUsers(siteId)
      dispatch(
        actions.fetchingSiteUsersSuccess({ siteId, siteUsers: response.data }),
      )
    } catch (error) {
      dispatch(actions.fetchingSiteUsersError(error))
    }
  }
}

export const fetchSiteAccessCodes = (siteId: number) => {
  return async (dispatch, getState: () => RootState) => {
    const state = getState()
    const accessCodesData = state.sitesManager.siteDetailsAccessCodes.data
    if (accessCodesData && accessCodesData[siteId]) return

    try {
      const response = await ssapi.getSiteAccessCodes(siteId)
      const accessCodes: string[] = Array.isArray(response.data)
        ? response.data
        : []
      dispatch(
        actions.fetchingAccessCodesSuccess({
          siteId,
          accessCodes,
        }),
      )
    } catch (error) {
      dispatch(
        addNotification({
          color: 'danger',
          text: getReadableErrorMessage(error, 'An unexpected error occurred'),
        }),
      )
    }
  }
}

export const createAccessCodes = (siteIds: number[]) => {
  return async dispatch => {
    dispatch(actions.creatingAccessCodes())
    try {
      const response = await ssapi.createAccessCodes(siteIds)
      dispatch(
        actions.creatingAccessCodesSuccess(
          response.data as PatchResponse<CreateAccessCodeResult>,
        ),
      )
    } catch (error) {
      dispatch(
        addNotification({
          color: 'danger',
          text: getReadableErrorMessage(error, 'An unexpected error occurred'),
        }),
      )
      dispatch(actions.creatingAccessCodesError())
    }
  }
}

export const createSiteUser = (
  siteUser: Partial<SiteUser>,
  sendEmail: boolean = true,
  selectedSite?: Site,
) => {
  return async dispatch => {
    dispatch(actions.creatingSiteUser())

    try {
      const response = await ssapi.createSiteUser(siteUser, sendEmail)
      const newSiteUser = response.data
      dispatch(
        addNotification({
          color: 'success',
          text: sendEmail
            ? `${newSiteUser.inviteEmail} was added to ${selectedSite.name} and was sent an email notification`
            : `${newSiteUser.inviteEmail} was added to ${selectedSite.name}`,
        }),
      )
      dispatch(actions.creatingSiteUserSuccess(response.data))
    } catch (error) {
      dispatch(
        addNotification({
          color: 'info',
          text: `The user ${siteUser.inviteEmail} is already a member of this site.`,
        }),
      )
      dispatch(actions.creatingSiteUserError(error))
    }
  }
}

export const createSiteUsers = (
  siteId: number,
  siteUsers: Partial<SiteUser>[],
  sendEmail: boolean,
) => {
  return async dispatch => {
    dispatch(actions.creatingSiteUsers())

    try {
      const response = await ssapi.bulkCreateSiteUsers(
        siteId,
        siteUsers,
        sendEmail,
      )
      const newSiteUsers = response.data.createdItems
      const failedSiteUsers = response.data.failedItems
      const invalidSiteUsers = response.data.invalidItems
      dispatch(
        actions.creatingSiteUsersSuccess({
          siteId,
          newSiteUsers,
          failedSiteUsers,
          invalidSiteUsers,
          sendEmail,
        }),
      )
    } catch (error) {
      dispatch(actions.creatingSiteUsersError(error))
    }
  }
}

export const updateSiteUser = (
  siteUser: SiteUser,
  notification: {
    successMessage: (params: { email: string; role?: string }) => string
    errorMessage: (params: { email: string; role?: string }) => string
  },
) => {
  return async dispatch => {
    dispatch(actions.updatingSiteUser())
    const msgParams = {
      role: siteUser.roles[0],
      email: siteUser.inviteEmail,
    }

    try {
      await ssapi.updateSiteUser(siteUser)
      dispatch(actions.updatingSiteUserSuccess(siteUser))
      dispatch(
        addNotification({
          color: 'success',
          text: notification.successMessage(msgParams),
        }),
      )
    } catch (error) {
      dispatch(actions.updatingSiteUserError(error))
      dispatch(
        addNotification({
          color: 'danger',
          text: notification.errorMessage(msgParams),
        }),
      )
    }
  }
}

export const remindSiteUser = (siteUser: SiteUser) => {
  return async dispatch => {
    dispatch(actions.remindingSiteUsers())

    try {
      await ssapi.remindSiteUser(siteUser)
      dispatch(
        addNotification({
          color: 'success',
          text: `You've sent a reminder to ${siteUser.inviteEmail}`,
        }),
      )
      dispatch(actions.remindingSiteUserSuccess())
    } catch (error) {
      dispatch(
        addNotification({
          color: 'danger',
          text: `We weren't able to remind ${siteUser.inviteEmail}. Please try again later.`,
        }),
      )
      dispatch(actions.remindingSiteUserError(error))
    }
  }
}

export const remindSiteUsers = (
  siteId: number,
  emailFilters: SendEmailRequest,
) => {
  return async dispatch => {
    dispatch(actions.remindingSiteUsers())

    try {
      await ssapi.remindSiteUsers(siteId, emailFilters)
      dispatch(
        addNotification({
          color: 'success',
          text: `Bulk notifications sent`,
        }),
      )
      dispatch(actions.remindingSiteUsersSuccess())
    } catch (error) {
      const errorMessage = getReadableErrorMessage(error, 'Unknown error')
      dispatch(
        addNotification({
          color: 'danger',
          text: `Something went wrong: ${errorMessage}`,
        }),
      )
      dispatch(actions.remindingSiteUsersError(error))
    }
  }
}

export const resetBulkAddUsers = () => {
  return async dispatch => {
    dispatch(actions.resetBulkAddUsers())
  }
}

export const bulkAddExternalSiteMapping = (file: File) => {
  return async dispatch => {
    dispatch(actions.bulkAddingExternalSiteMapping())
    const bodyFormData = new FormData()
    bodyFormData.set('externalSiteMaps', file)

    try {
      const response = await ssapi.bulkAddExternalSiteMapping(bodyFormData)
      dispatch(actions.bulkAddingExternalSiteMappingSuccess(response.data))
    } catch (error) {
      const errorMessage = getReadableErrorMessage(error, 'Unknown error')
      dispatch(actions.bulkAddingExternalSiteMappingError(errorMessage))
      dispatch(
        addNotification({
          color: 'danger',
          text: errorMessage,
        }),
      )
    }
  }
}

export const clearSites = () => {
  return async dispatch => {
    dispatch(actions.clearSites())
  }
}

export const clearSiteCache = (siteId: number) => {
  return async dispatch => {
    try {
      await ssapi.clearCache({ siteId })
      dispatch(
        addNotification({
          color: 'success',
          text: 'Site cache cleared successfully.',
        }),
      )
    } catch (error) {
      dispatch(
        addNotification({
          color: 'danger',
          text: getReadableErrorMessage(error, 'Failed to clear site cache.'),
        }),
      )
    }
  }
}

export const siteOrgNetSuiteCustomerSearch = ({
  nsCustomerId,
  orgId,
}: {
  nsCustomerId: string
  orgId: number
}) => {
  return new Promise<AxiosResponse<Site[]>>((resolve, reject) =>
    siteOrgNetSuiteCustomerSearchDebounced({
      nsCustomerId,
      orgId,
      resolve,
      reject,
    }),
  )
}

const siteOrgNetSuiteCustomerSearchDebounced = _.debounce(
  async ({
    nsCustomerId,
    orgId,
    resolve,
    reject,
  }: {
    nsCustomerId: string
    orgId: number
    resolve: (value: AxiosResponse<Site[]>) => void
    reject: (reason?: unknown) => void
  }) => {
    try {
      const response = await ssapi.siteOrgNetSuiteCustomerSearch(
        nsCustomerId,
        orgId,
      )
      resolve(response)
    } catch (err) {
      reject(err)
    }
  },
  300,
)

export default {
  clearSites,
  createAccessCodes,
  createSiteUser,
  createSiteUsers,
  fetchSite,
  fetchSites,
  fetchSiteUsers,
  mergeSite,
  updateSite,
  updateSiteUser,
  remindSiteUser,
  remindSiteUsers,
  resetBulkAddUsers,
}
